import * as vp from 'vopay';
import { FunctionComponent, useEffect } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import useFetchTransactionsHistory from '@/hooks/useApi/useFetchTransactionsHistory';

const { Table, Box, SubTitle } = vp;

type CurrencyConversionTransactionInfoProps = {
  transaction: vp.CurrencyConversionTransactionList;
};

type FC = FunctionComponent<CurrencyConversionTransactionInfoProps>;

type KeyValuePair = {
  display: string;
  value: string;
  visible?: boolean;
};

type TablePropsHandlers = {
  transactionDetails: () => vp.TableProps;
};

const CurrencyConversionTransactionInfo: FC = (props: CurrencyConversionTransactionInfoProps) => {
  const { transaction } = props;
  const currentDate = moment().utc().format('YYYY-MM-DD');
  const { transactions, fetchTransactionsHistory } = useFetchTransactionsHistory();

  const { t } = useTranslation('translations', {
    keyPrefix: 'Transaction.TransactionHistory.Details.CurrencyConversionTransactionInfo',
  });

  useEffect(() => {
    fetchTransactionsHistory({
      StartDateTime: moment().subtract(5, 'years').format('YYYY-MM-DD'),
      EndDateTime: currentDate,
      TransactionID: transaction.TransactionID,
      IncludeSubaccountTransactions: true,
    });
  }, [transaction.TransactionID, currentDate]);

  return (
    <div>
      <Box mt="lg" mb="md">
        <SubTitle subTitle={t('transactionDetailsTitle') || ''} />
      </Box>
      <Table {...getTableProps('transactionDetails')} />
    </div>
  );

  function getTableProps(handler: keyof TablePropsHandlers): vp.TableProps {
    const handlers: TablePropsHandlers = {
      transactionDetails: () => {
        const accountDetailPairs: KeyValuePair[] = [
          {
            display: t('transactionDetails.transactionID'),
            value: transaction.TransactionID,
            visible: true,
          },
          {
            display: t('transactionDetails.sellAmount'),
            value: `$${transaction.SellAmount}`,
            visible: true,
          },
          {
            display: t('transactionDetails.buyAmount'),
            value: `$${transaction.BuyAmount}`,
            visible: true,
          },
          {
            display: t('transactionDetails.conversionRate'),
            value: transaction.ConversionRate,
            visible: true,
          },
          {
            display: t('transactionDetails.notes'),
            value: transactions.map((data) => data.Notes).pop() || '',
            visible: !!transactions.map((data) => data.Notes).pop(),
          },
        ];

        const accountInfoProps: vp.TableProps = {
          name: 'transaction-details',
          variant: 'pair',
          columns: {
            detailKey: { header: t('transactionDetails.header') || '' },
            detailValue: { header: '' },
          },
          rows: accountDetailPairs
            .filter(({ visible }) => visible)
            .map(({ display, value }) => ({
              detailKey: display,
              detailValue: value,
            })),
        };

        return accountInfoProps;
      },
    };

    return handlers[handler]();
  }
};

export default CurrencyConversionTransactionInfo;
